import styles from './DeadlinesTable.module.css';
import Input from '../../../../components/Input/Input';
import {CustomSelect} from '../../../../components/Select/Select';
import UniversalTable from '../../../../components/UTable/UTable';
import Button from '../../../../components/Button/Button';
import IconButton from '../../../../components/IconButton/IconButton';
import {Modal} from '../../../../components/Modals/Modal';
import {GeneratePersonalDocsModal} from '../../../../components/Modals/GeneratePersonalDocsModal/GeneratePersonalDocsModal';
import {GenerateOrgDocsModal} from '../../../../components/Modals/GenerateOrgDocsModal/GenerateOrgDocsModal';
import {
	getAllDivisionsUrl,
	getAllFileTemplatesUrl,
	getAllProfessionsUrl,
	getAllTemplateBindsUrl,
	getDocumentDeadlines,
	OrganizationDocumentsLabel,
	OrganizationDocumentsValue
} from '../../../../helpers/constants';
import React, {useEffect, useMemo, useState} from 'react';
import useFillSelect from '../../../../hooks/useFillSelect.hook';
import useTemplateRegistry from '../../../../hooks/useTemplateRegistry.hook';
import ErrorMessage from '../../../../components/ErrorMessage/ErrorMessage';
import {buildColumns} from './table_columns';

const normalize = id => (id === undefined || id === null) ? id : String(id);

export function DeadlinesTable({presetTemplateId}) {
	const [totalRecords, setTotalRecords] = useState(0);
	const [lastNameFilter, setLastNameFilter] = useState(undefined);
	const [selectedProfOption, setSelectedProfOption] = useState(null);
	const [selectedDivOption, setSelectedDivOption] = useState(null);
	const [selectedTemplateOption, setSelectedTemplateOption] = useState(null);
	const [errorApi, setErrorApi] = useState(undefined);
	const [selectedKeys, setSelectedKeys] = useState([]);
	const [isGenerateModalOpen, setGenerateModalOpen] = useState(false);
	const [isGenerating, setIsGenerating] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);

	const {
		itemDict: FileTemplatesDict
	} = useFillSelect({
		endpoint: getAllFileTemplatesUrl,
		labelField: 'file_name'
	});

	const {byCode: templatesByCode, groupTemplates, error: errorRegistry} = useTemplateRegistry();
	const personalTemplateCodes = new Set(groupTemplates('personal').map(t => t.code));
	const columns = useMemo(() => buildColumns(templatesByCode), [templatesByCode]);

	const optionsTemplates = [
		...Object.values(FileTemplatesDict)
			.filter(file => file.period != null && personalTemplateCodes.has(file.file_name))
			.map(file => ({
				value: String(file.id),
				label: templatesByCode[file.file_name]?.title || file.file_name
			}))
			.sort((a, b) => a.label.localeCompare(b.label, 'ru')),
		{value: 0, label: OrganizationDocumentsLabel}
	];

	useEffect(() => {
		const templatesLoaded = Object.keys(FileTemplatesDict).length > 0 && personalTemplateCodes.size > 0;
		if (!templatesLoaded || optionsTemplates.length === 0) {
			return;
		}
		if (presetTemplateId != null) {
			const option = optionsTemplates.find(
				(item) => String(item.value) === String(presetTemplateId)
			);
			if (option) {
				setSelectedTemplateOption(option);
			}
			return;
		}
		setSelectedTemplateOption((prev) => prev ?? optionsTemplates[0]);
	}, [presetTemplateId, optionsTemplates.length, FileTemplatesDict, personalTemplateCodes.size]);

	const {
		error: errorProf,
		options: allOptionsProf
	} = useFillSelect({
		endpoint: getAllProfessionsUrl,
		labelField: 'title'
	});

	const {
		itemDict: bindsDict
	} = useFillSelect({
		endpoint: getAllTemplateBindsUrl,
		labelField: 'id'
	});

	const isOrgDocuments = !selectedTemplateOption
		|| selectedTemplateOption.value === 0
		|| String(selectedTemplateOption.value) === '0'
		|| selectedTemplateOption.value === OrganizationDocumentsValue;
	const isPersonalTemplate = Boolean(selectedTemplateOption) && !isOrgDocuments;
	const isOrgSelected = Boolean(selectedTemplateOption) && isOrgDocuments;
	const canGenerate = isPersonalTemplate || isOrgSelected;

	const optionsProf = useMemo(() => {
		if (isOrgDocuments) {
			return allOptionsProf;
		}
		const boundProfIds = new Set(
			Object.values(bindsDict)
				.filter(bind => String(bind.file_template_id) === String(selectedTemplateOption.value))
				.map(bind => String(bind.profession_id))
		);
		return allOptionsProf.filter(p => boundProfIds.has(p.value));
	}, [isOrgDocuments, selectedTemplateOption, bindsDict, allOptionsProf]);

	const {
		error: errorDiv,
		options: optionsDiv
	} = useFillSelect({
		endpoint: getAllDivisionsUrl,
		labelField: 'title'
	});

	useEffect(() => {
		setErrorApi(errorProf || errorDiv || errorRegistry);
	}, [errorProf, errorDiv, errorRegistry]);

	useEffect(() => {
		if (selectedProfOption && !optionsProf.find(p => p.value === selectedProfOption.value)) {
			setSelectedProfOption(null);
		}
	}, [optionsProf]);

	useEffect(() => {
		setSelectedKeys([]);
	}, [selectedTemplateOption?.value]);

	const [debouncedLastName, setDebouncedLastName] = useState(lastNameFilter);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedLastName(lastNameFilter);
		}, 500);
		return () => clearTimeout(timer);
	}, [lastNameFilter]);

	let filters = {};
	if (lastNameFilter) {
		filters = {...filters, user__last_name__ilike: `%${debouncedLastName}%`};
	}
	if (selectedProfOption) {
		filters = {...filters, user__profession_id__in: selectedProfOption.value};
	}
	if (selectedDivOption) {
		filters = {...filters, user__division_id__in: selectedDivOption.value};
	}
	if (selectedTemplateOption) {
		filters = {...filters, template_id__in: selectedTemplateOption.value};
	}

	const ClearFilters = () => {
		setSelectedProfOption(null);
		setSelectedDivOption(null);
		setLastNameFilter('');
		setSelectedTemplateOption(null);
		setSelectedKeys([]);
	};

	const getSelectedNormalized = () => Array.isArray(selectedKeys) ? selectedKeys.map(normalize) : [];

	const addIds = (ids) => {
		setSelectedKeys(prev => {
			const prevArr = Array.isArray(prev) ? prev.map(normalize) : [];
			const set = new Set(prevArr);
			ids.forEach(id => {
				const n = normalize(id);
				if (n !== undefined && n !== null) set.add(n);
			});
			return Array.from(set);
		});
	};

	const removeIds = (ids) => {
		setSelectedKeys(prev => {
			const prevArr = Array.isArray(prev) ? prev.map(normalize) : [];
			const set = new Set(prevArr);
			ids.forEach(id => {
				const n = normalize(id);
				set.delete(n);
			});
			return Array.from(set);
		});
	};

	const rowSelection = {
		selectedRowKeys: getSelectedNormalized(),
		preserveSelectedRowKeys: true,
		onSelect: (record, selected) => {
			const id = record.id ?? record.key;
			if (selected) addIds([id]);
			else removeIds([id]);
		},
		onSelectAll: (selected, selectedRows, changeRows) => {
			const rows = (changeRows && changeRows.length) ? changeRows : (selectedRows || []);
			const ids = rows.map(r => r.id ?? r.key);
			if (selected) addIds(ids);
			else removeIds(ids);
		}
	};

	const selectedFile = isPersonalTemplate
		? FileTemplatesDict[String(selectedTemplateOption.value)]
		: null;
	const selectedTemplate = selectedFile ? templatesByCode[selectedFile.file_name] : undefined;

	const handleCloseGenerateModal = () => {
		if (isGenerating) return;
		setGenerateModalOpen(false);
	};

	const handleGenerateSuccess = () => {
		setSelectedKeys([]);
		setRefreshKey(prev => prev + 1);
		setGenerateModalOpen(false);
		setIsGenerating(false);
	};

	return (
		<div className={styles.outer_table}>
			<h2 className={styles.section_title}>Список оформленных документов</h2>
			<ErrorMessage
				error={errorApi}
				onClose={() => setErrorApi(undefined)}
			/>
			<div className={styles.table_toolbar}>
				<div className={styles.filters_table}>
					<CustomSelect
						value={selectedTemplateOption}
						options={optionsTemplates}
						placeholder="Шаблон"
						onChange={setSelectedTemplateOption}
					/>
					<Input
						value={lastNameFilter}
						onChange={(e) => setLastNameFilter(e.target.value)}
						placeholder="Поиск по фамилии"
					/>
					<CustomSelect
						value={selectedProfOption}
						options={optionsProf}
						placeholder="Профессия"
						onChange={setSelectedProfOption}
					/>
					<CustomSelect
						value={selectedDivOption}
						options={optionsDiv}
						placeholder="Подразделение"
						onChange={setSelectedDivOption}
					/>
					<IconButton onClick={ClearFilters} title="очистить фильтр">
						<img src="/icons/drop-filters-icon.svg" alt="очистить фильтр"/>
					</IconButton>
				</div>
				<div className={styles.table_actions}>
					{canGenerate && selectedKeys.length > 0 && (
						<span className={styles.selected_count}>Выбрано: {selectedKeys.length}</span>
					)}
					<div className={styles.table_count}>
						Записей: {totalRecords}
					</div>
					{canGenerate && (
						<Button
							disabled={selectedKeys.length === 0}
							onClick={() => setGenerateModalOpen(true)}
						>
							Сформировать
						</Button>
					)}
				</div>
			</div>
			<div className={styles.table}>
				<UniversalTable
					endpoint={getDocumentDeadlines}
					columns={columns}
					usePagination={false}
					initialPage={1}
					initialPageSize={15}
					filters={filters}
					onTotalRecordsChange={setTotalRecords}
					refreshKey={refreshKey}
					tableProps={canGenerate ? {rowSelection} : {}}
				/>
			</div>
			<Modal isOpen={isGenerateModalOpen} onClose={handleCloseGenerateModal}>
				{isGenerateModalOpen && selectedTemplate && (
					<GeneratePersonalDocsModal
						template={selectedTemplate}
						selectedUsers={getSelectedNormalized()}
						onClose={handleCloseGenerateModal}
						onSuccess={handleGenerateSuccess}
						onLoadingChange={setIsGenerating}
					/>
				)}
				{isGenerateModalOpen && isOrgSelected && (
					<GenerateOrgDocsModal
						templateCodes={getSelectedNormalized()}
						onSuccess={handleGenerateSuccess}
						onLoadingChange={setIsGenerating}
					/>
				)}
			</Modal>
		</div>
	);
}
